import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { DISTRIBUTION_PRESETS, type DistributionId } from '../data/distribution-presets';
import { pascalRow } from '../prob/counting';
import {
  binomialPmf,
  distributionMoments,
  geometricPmf,
  hypergeometricPmf,
  poissonPmf,
} from '../prob/distributions';
import { createPmfChart, type PmfChartHandle } from '../render/pmf-chart';
import { createSvgEl } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow, createToggleRow, type SliderRowHandle } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';

// ============================================================================
// Exported helper (tested)
// ============================================================================

export function buildBarsFor(
  spec:
    | { kind: 'binomial'; n: number; p: number }
    | { kind: 'geometric'; p: number }
    | { kind: 'poisson'; lambda: number }
    | { kind: 'hypergeometric'; N: number; K: number; n: number },
): { x: number; p: number }[] {
  const bars: { x: number; p: number }[] = [];
  switch (spec.kind) {
    case 'binomial':
      for (let k = 0; k <= spec.n; k++) bars.push({ x: k, p: binomialPmf(spec.n, spec.p, k) });
      break;
    case 'geometric':
      for (let k = 1; k <= 30; k++) bars.push({ x: k, p: geometricPmf(spec.p, k) });
      break;
    case 'poisson':
      for (let k = 0; k <= Math.ceil(spec.lambda * 4); k++)
        bars.push({ x: k, p: poissonPmf(spec.lambda, k) });
      break;
    case 'hypergeometric':
      for (let k = 0; k <= spec.n; k++)
        bars.push({ x: k, p: hypergeometricPmf(spec.N, spec.K, spec.n, k) });
      break;
  }
  return bars;
}

// ============================================================================
// State
// ============================================================================

interface State {
  id: DistributionId;
  params: Record<string, number>;
  showCdf: boolean;
  showMoments: boolean;
  showPascal: boolean;
}

// ============================================================================
// Pure chart renderer (shared between initial draw and update)
// ============================================================================

function buildChartOpts(
  state: State,
  i18n: ProbContext['i18n'],
): import('../render/pmf-chart').PmfChartOptions {
  const { id, params, showCdf, showMoments, showPascal } = state;
  const spec =
    id === 'binomial'
      ? ({ kind: 'binomial', n: params['n']!, p: params['p']! } as const)
      : id === 'geometric'
        ? ({ kind: 'geometric', p: params['p']! } as const)
        : id === 'poisson'
          ? ({ kind: 'poisson', lambda: params['lambda']! } as const)
          : ({
              kind: 'hypergeometric',
              N: params['N']!,
              K: params['K']!,
              n: params['n']!,
            } as const);
  const bars = buildBarsFor(spec);
  const moments = distributionMoments(spec);
  const chartOpts: import('../render/pmf-chart').PmfChartOptions = {
    xDomain: [Math.min(...bars.map((b) => b.x)) - 0.5, Math.max(...bars.map((b) => b.x)) + 0.5],
    bars,
    showCdf,
    xLabelKey: 'axis.outcome',
    yLabelKey: showCdf ? 'axis.probability-cumulative' : 'axis.probability',
    i18n,
  };
  if (showMoments) {
    chartOpts.markers = [
      { x: moments.mean, labelKey: 'stat.expectation' },
      { x: moments.mean + Math.sqrt(moments.variance), labelKey: 'stat.stdev' },
      { x: moments.mean - Math.sqrt(moments.variance), labelKey: 'stat.stdev' },
    ];
  }
  if (showPascal && id === 'binomial') {
    chartOpts.pascalRowValues = pascalRow(params['n']!);
  }
  return chartOpts;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.named-distributions.label',
    demoId: 'probability',
    advancedKey: 'named-distributions',

    initialState(): State {
      return {
        id: 'binomial',
        params: { ...DISTRIBUTION_PRESETS[0]!.defaults },
        showCdf: false,
        showMoments: false,
        showPascal: false,
      };
    },

    createRender(ctx, host, controls) {
      // ── Stage SVG (appended to host = ctx.root) ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 420', class: 'pb-nd-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // pmf-chart handle — replaced on every state change, null initially.
      let chart: PmfChartHandle | null = null;

      // ── Panel: distribution <select> ──
      const distLbl = document.createElement('label');
      distLbl.textContent = ctx.i18n.t('param.submode.label');
      const select = document.createElement('select');
      for (const p of DISTRIBUTION_PRESETS) {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = ctx.i18n.t(p.labelKey);
        select.appendChild(opt);
      }
      select.addEventListener('change', () => {
        const newId = select.value as DistributionId;
        const newParams = { ...DISTRIBUTION_PRESETS.find((p) => p.id === newId)!.defaults };
        controls.setState((s) => ({ ...s, id: newId, params: newParams }));
      });
      const distributionRow = document.createElement('div');
      distributionRow.className = 'pb-dropdown-row';
      distributionRow.append(distLbl, select);
      ctx.panel.appendChild(distributionRow);

      // ── Panel: param sliders (section rebuilt when distribution changes) ──
      // We keep a stable <div> wrapper so we can replace only the slider section.
      const sliderSection = document.createElement('div');
      ctx.panel.appendChild(sliderSection);

      // Slider handles for the CURRENT distribution — replaced when distribution changes.
      let paramHandles: Record<string, SliderRowHandle> = {};

      // Builds the slider section for the given distribution id + params.
      // Called once on createRender and again whenever update() detects an id change.
      let currentRenderedId: DistributionId | null = null;

      function buildSliderSection(id: DistributionId, params: Record<string, number>): void {
        sliderSection.replaceChildren();
        paramHandles = {};
        const N = params['N'] ?? 0;
        const labelKeyFor: Record<string, ProbKey> = {
          n: 'param.n.label',
          p: 'param.p.label',
          lambda: 'param.lambda.label',
          N: 'param.N-population.label',
          K: 'param.K-successes.label',
        };
        for (const key of Object.keys(params)) {
          let min: number;
          let max: number;
          let step: number;
          let format: ((v: number) => string) | undefined;
          if (key === 'p') {
            min = 0.01;
            max = 0.99;
            step = 0.01;
            format = (v) => v.toFixed(2);
          } else if (key === 'lambda') {
            min = 0.1;
            max = 20;
            step = 0.1;
            format = (v) => v.toFixed(1);
          } else if (id === 'hypergeometric' && key === 'N') {
            min = 2;
            max = 50;
            step = 1;
          } else if (id === 'hypergeometric' && (key === 'K' || key === 'n')) {
            min = 1;
            max = Math.max(1, N);
            step = 1;
          } else {
            min = 1;
            max = 50;
            step = 1;
          }
          const labelKey = labelKeyFor[key];
          if (!labelKey) continue;
          const row = createSliderRow({
            id: `nd-${key}`,
            label: ctx.i18n.t(labelKey),
            i18n: ctx.i18n,
            min,
            max,
            step,
            value: params[key]!,
            format: format ?? paramFormat,
            onChange: (v) => {
              controls.setState((s) => {
                const nextParams = { ...s.params, [key]: v };
                // Cross-clamp: when N changes, cap K and n in the model too.
                if (s.id === 'hypergeometric' && key === 'N') {
                  const newN = v;
                  for (const dep of ['K', 'n'] as const) {
                    if ((nextParams[dep] ?? 0) > newN) {
                      nextParams[dep] = newN;
                    }
                  }
                }
                return { ...s, params: nextParams };
              });
            },
          });
          sliderSection.appendChild(row.element);
          paramHandles[key] = row;
        }
        currentRenderedId = id;
      }

      // ── Panel: checkboxes (always present, stable) ──
      const checkboxSection = document.createElement('div');
      ctx.panel.appendChild(checkboxSection);

      // We manage three checkboxes; pascal is conditionally shown for binomial.
      function makeCheckbox(
        labelKey: ProbKey,
        initial: boolean,
        onChange: (v: boolean) => void,
      ): ReturnType<typeof createToggleRow> {
        return createToggleRow({ labelKey, i18n: ctx.i18n, checked: initial, onChange });
      }

      const cdfCb = makeCheckbox('param.show-cdf.label', false, (v) =>
        controls.setState((s) => ({ ...s, showCdf: v })),
      );
      const momentsCb = makeCheckbox('param.show-moments.label', false, (v) =>
        controls.setState((s) => ({ ...s, showMoments: v })),
      );
      const pascalCb = makeCheckbox('param.show-pascal-row.label', false, (v) =>
        controls.setState((s) => ({ ...s, showPascal: v })),
      );

      checkboxSection.append(cdfCb.element, momentsCb.element);
      // Pascal checkbox appended/removed by update() depending on distribution id.
      let pascalInDom = false;

      // ── RenderHandle ──
      const update = (state: State): void => {
        const { id, params, showCdf, showMoments, showPascal } = state;

        // Reflect select.
        select.value = id;

        // Rebuild slider section only when the distribution id changes.
        if (id !== currentRenderedId) {
          buildSliderSection(id, params);
        } else {
          // Just sync slider values and apply cross-clamp to slider UI.
          for (const [key, h] of Object.entries(paramHandles)) {
            if (params[key] !== undefined) {
              h.setValue(params[key]);
            }
          }
          // Cross-clamp slider max/value for hypergeometric K and n when N changed.
          if (id === 'hypergeometric') {
            const N = params['N'] ?? 0;
            for (const dep of ['K', 'n'] as const) {
              const h = paramHandles[dep];
              if (!h) continue;
              h.input.max = String(N);
              if ((params[dep] ?? 0) > N) {
                h.setValue(N);
              }
            }
          }
        }

        // Pascal checkbox: show only for binomial.
        if (id === 'binomial' && !pascalInDom) {
          checkboxSection.appendChild(pascalCb.element);
          pascalInDom = true;
        } else if (id !== 'binomial' && pascalInDom) {
          pascalCb.element.remove();
          pascalInDom = false;
        }

        // Reflect checkbox states.
        cdfCb.setChecked(showCdf);
        momentsCb.setChecked(showMoments);
        pascalCb.setChecked(showPascal);

        // Destroy and recreate pmf-chart.
        chart?.destroy();
        stageGroup.replaceChildren();
        chart = createPmfChart(stageGroup, buildChartOpts(state, ctx.i18n));
      };

      return {
        update,
        dispose() {
          chart?.destroy();
          chart = null;
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Registry alias — keeps index.ts and the mount-based registry green without edits.
export const mountNamedDistributions = experiment.mount.bind(experiment);
